import { useEffect, useState } from "react";
import api from "@/lib/api";
import { downloadXlsx } from "@/lib/download";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { X, Loader2, FileSpreadsheet, FileText, Sparkles, Handshake, AlertTriangle } from "lucide-react";

const fmtEur = (n) => `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const SOURCE = { vademecum: "vademécum", linear: "lineal", excluded: "excluida" };
const NEG_OPTIONS = [5, 10, 15, 20, 25, 30, 35, 40];

export default function Top25BestOptionModal({ dispensationId, label, onClose, initialBasis = "marca" }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [basis, setBasis] = useState(initialBasis);
  const [negPct, setNegPct] = useState(20);
  const [tab, setTab] = useState("best");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get(`/dispensations/${dispensationId}/top25-best-option`, {
          params: { basis, neg_pct: negPct },
        });
        if (alive) setData(res.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudo calcular la opción más rentable");
        onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispensationId, basis, negPct]);

  const t = data?.totals || {};
  const exportUrl = (fmt) =>
    `/dispensations/${dispensationId}/top25-best-option?export=${fmt}&basis=${basis}&neg_pct=${negPct}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="top25-best-option-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[1240px] my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-emerald-600" />
              Opción más rentable del Top 25
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Base del cálculo: € total de la molécula (marca + genérico) · solo laboratorios con condiciones cargadas
              {label ? ` · ${label}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadXlsx(exportUrl("excel"), "top25_opcion_mas_rentable.xlsx")}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 rounded-lg"
              data-testid="top25-best-export-excel"
            >
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </button>
            <button
              onClick={() => downloadXlsx(exportUrl("pdf"), "top25_opcion_mas_rentable.pdf")}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-rose-700 border border-rose-200 bg-rose-50 hover:bg-rose-100 px-3 py-2 rounded-lg"
              data-testid="top25-best-export-pdf"
            >
              <FileText className="h-4 w-4" /> PDF
            </button>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700" data-testid="top25-best-close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 px-6 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mr-1">Ranking</span>
            {[
              { v: "marca", t: "Top 25 € marca" },
              { v: "generico", t: "Top 25 € genérico" },
              { v: "unidades", t: "Top 25 más dispensadas" },
            ].map((o) => (
              <button
                key={o.v}
                onClick={() => setBasis(o.v)}
                className={`text-xs font-bold px-3 py-1.5 rounded-lg border-2 transition-colors ${
                  basis === o.v ? "border-emerald-500 bg-white text-emerald-700" : "border-transparent bg-white text-slate-500 hover:text-slate-800"
                }`}
                data-testid={`top25-basis-${o.v}`}
              >
                {o.t}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            {[
              { v: "best", t: "Mejor opción" },
              { v: "pending", t: `Negociar pendientes (${t.n_pending_labs || 0})` },
            ].map((o) => (
              <button
                key={o.v}
                onClick={() => setTab(o.v)}
                className={`text-xs font-bold px-3 py-1.5 rounded-lg ${
                  tab === o.v ? "bg-slate-900 text-white" : "bg-white text-slate-500 border border-slate-200 hover:text-slate-800"
                }`}
                data-testid={`top25-tab-${o.v}`}
              >
                {o.t}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex flex-col items-center gap-3 text-slate-500 text-sm">
            <Loader2 className="h-6 w-6 animate-spin" />
            Calculando la mejor opción entre tus laboratorios…
          </div>
        ) : !data ? null : tab === "best" ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 px-6 py-4 bg-slate-50 border-b border-slate-200">
              {[
                { k: "Ahorro €/mes", v: fmtEur(t.saving_month_eur), hi: true, tid: "kpi-saving-month" },
                { k: "Ahorro €/año", v: fmtEur(t.saving_year_eur), hi: true, tid: "kpi-saving-year" },
                { k: "Beneficio actual", v: fmtEur(t.benefit_current_eur), tid: "kpi-benefit-current" },
                { k: "Beneficio óptimo", v: fmtEur(t.benefit_best_eur), tid: "kpi-benefit-best" },
                { k: "Mejorables", v: `${t.n_with_saving || 0} / ${t.n_molecules || 0}`, tid: "kpi-improvable" },
              ].map((c) => (
                <div
                  key={c.k}
                  className={`rounded-xl border p-3 ${c.hi ? "bg-emerald-600 border-emerald-700 text-white" : "bg-white border-slate-200"}`}
                  data-testid={c.tid}
                >
                  <div className={`text-[10px] uppercase tracking-widest font-bold ${c.hi ? "text-emerald-100" : "text-slate-500"}`}>{c.k}</div>
                  <div className="font-mono-num font-bold text-lg mt-0.5">{c.v}</div>
                </div>
              ))}
            </div>

            <div className="max-h-[58vh] overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-500 uppercase text-[10px] tracking-wider sticky top-0">
                  <tr>
                    <th className="p-2 text-left">#</th>
                    <th className="p-2 text-left">Molécula (G.H.)</th>
                    <th className="p-2 text-right">Uds</th>
                    <th className="p-2 text-right">€ marca</th>
                    <th className="p-2 text-right">€ genérico</th>
                    <th className="p-2 text-right">€ total</th>
                    <th className="p-2 text-left">Lab actual</th>
                    <th className="p-2 text-right">Dto. actual</th>
                    <th className="p-2 text-left">Mejor lab con condiciones</th>
                    <th className="p-2 text-right">Dto. mejor</th>
                    <th className="p-2 text-right">Ahorro €</th>
                    <th className="p-2 text-right">€/mes</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.gh} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`top25-best-row-${r.gh}`}>
                      <td className="p-2 text-slate-400 font-mono-num">{r.rank}</td>
                      <td className="p-2 font-semibold text-slate-800 max-w-[260px] truncate" title={r.gh}>{r.gh}</td>
                      <td className="p-2 text-right font-mono-num text-slate-500">{(r.total_units || 0).toLocaleString("es-ES")}</td>
                      <td className="p-2 text-right font-mono-num text-slate-500">{fmtEur(r.marca_euros)}</td>
                      <td className="p-2 text-right font-mono-num text-slate-500">{fmtEur(r.generico_euros)}</td>
                      <td className="p-2 text-right font-mono-num">{fmtEur(r.total_euros)}</td>
                      <td className="p-2 text-slate-600 max-w-[140px] truncate" title={r.current_lab || ""}>{r.current_lab || "—"}</td>
                      <td className="p-2 text-right font-mono-num">{r.current_pct}%</td>
                      <td className="p-2 max-w-[170px] truncate">
                        {r.best_lab ? (
                          <span className="font-semibold text-emerald-700" title={r.best_lab}>{r.best_lab}</span>
                        ) : (
                          <span className="text-rose-600 font-semibold">Sin condición cargada</span>
                        )}
                        {r.best_source && (
                          <span className="ml-1 text-[10px] text-slate-400 uppercase">{SOURCE[r.best_source] || ""}</span>
                        )}
                      </td>
                      <td className="p-2 text-right font-mono-num">{r.best_pct}%</td>
                      <td className={`p-2 text-right font-mono-num font-bold ${r.saving_eur > 0 ? "text-emerald-700" : "text-slate-400"}`}>
                        {r.saving_eur > 0 ? "+" : ""}{fmtEur(r.saving_eur)}
                      </td>
                      <td className={`p-2 text-right font-mono-num ${r.saving_eur > 0 ? "text-emerald-700" : "text-slate-400"}`}>
                        {fmtEur(r.saving_month_eur)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-900 text-white sticky bottom-0">
                  <tr data-testid="top25-best-total-row">
                    <td className="p-2" colSpan={5}>TOTAL Top 25</td>
                    <td className="p-2 text-right font-mono-num">{fmtEur(t.base_eur)}</td>
                    <td className="p-2 text-right text-[10px] text-slate-300" colSpan={4}>
                      {t.n_no_agreement || 0} sin condición · {t.n_already_best || 0} ya en el mejor
                    </td>
                    <td className="p-2 text-right font-mono-num font-bold text-emerald-300">{fmtEur(t.saving_eur)}</td>
                    <td className="p-2 text-right font-mono-num font-bold text-emerald-300">{fmtEur(t.saving_month_eur)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 bg-amber-50 border-b border-amber-200">
              <div className="text-xs text-amber-900">
                <div className="font-bold flex items-center gap-1.5">
                  <Handshake className="h-3.5 w-3.5" />
                  {t.n_pending_labs || 0} laboratorio(s) sin condición cargada comercializan moléculas de tu Top 25
                </div>
                <div className="mt-0.5">
                  <strong className="font-mono-num">{fmtEur(t.pending_eur_at_stake)}</strong> en juego · ya les compras{" "}
                  <strong className="font-mono-num">{fmtEur(t.pending_eur_current_purchase)}</strong> sin tener nada pactado
                </div>
              </div>
              <label className="text-xs font-semibold text-amber-900 flex items-center gap-2">
                Si negociaras un
                <select
                  value={negPct}
                  onChange={(e) => setNegPct(Number(e.target.value))}
                  className="border border-amber-300 rounded-md px-2 py-1 bg-white text-slate-800"
                  data-testid="top25-neg-pct"
                >
                  {NEG_OPTIONS.map((p) => <option key={p} value={p}>{p}%</option>)}
                </select>
              </label>
            </div>

            <div className="max-h-[58vh] overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-500 uppercase text-[10px] tracking-wider sticky top-0">
                  <tr>
                    <th className="p-2 text-left">Laboratorio</th>
                    <th className="p-2 text-right">Moléculas Top 25</th>
                    <th className="p-2 text-right">€ en juego</th>
                    <th className="p-2 text-right">€ que ya le compras</th>
                    <th className="p-2 text-right">Ahorro al {negPct}%</th>
                    <th className="p-2 text-right">€/mes</th>
                    <th className="p-2 text-left">Principales moléculas</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data.pending_labs || []).map((p) => (
                    <tr
                      key={p.lab}
                      className={`border-b border-slate-100 ${p.is_current_supplier ? "bg-rose-50" : "hover:bg-slate-50"}`}
                      data-testid={`top25-pending-row-${p.lab}`}
                    >
                      <td className="p-2 font-semibold text-slate-800">
                        {p.lab}
                        {p.is_current_supplier && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-rose-700">
                            <AlertTriangle className="h-3 w-3" /> le compras sin condición
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-right font-mono-num">{p.n_molecules}</td>
                      <td className="p-2 text-right font-mono-num font-bold">{fmtEur(p.eur_at_stake)}</td>
                      <td className={`p-2 text-right font-mono-num ${p.eur_current_purchase > 0 ? "text-rose-700 font-bold" : "text-slate-400"}`}>
                        {fmtEur(p.eur_current_purchase)}
                      </td>
                      <td className="p-2 text-right font-mono-num font-bold text-emerald-700">{fmtEur(p.potential_eur)}</td>
                      <td className="p-2 text-right font-mono-num text-emerald-700">{fmtEur(p.potential_month_eur)}</td>
                      <td className="p-2 text-slate-500 max-w-[320px] truncate" title={(p.molecules || []).map((m) => m.gh).join(" · ")}>
                        {(p.molecules || []).slice(0, 3).map((m) => m.gh).join(" · ")}
                      </td>
                      <td className="p-2 text-right">
                        <Link
                          to="/proveedores/condiciones"
                          className="text-[11px] font-bold text-sky-700 hover:text-sky-900 whitespace-nowrap"
                          data-testid={`top25-pending-load-${p.lab}`}
                        >
                          Cargar condición →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-900 text-white sticky bottom-0">
                  <tr data-testid="top25-pending-total-row">
                    <td className="p-2">TOTAL pendientes</td>
                    <td className="p-2"></td>
                    <td className="p-2 text-right font-mono-num font-bold">{fmtEur(t.pending_eur_at_stake)}</td>
                    <td className="p-2 text-right font-mono-num font-bold text-rose-300">{fmtEur(t.pending_eur_current_purchase)}</td>
                    <td className="p-2 text-right font-mono-num font-bold text-emerald-300">
                      {fmtEur((t.pending_eur_at_stake || 0) * negPct / 100)}
                    </td>
                    <td className="p-2" colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
